import { useMemo, useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { LuPlus, LuTrash2 } from "react-icons/lu";
import { loadServices, PCP_TAXONOMY } from "../serviceCatalog.js";
import { adjudicateClaim, networkStatus, serviceName } from "./adjudicate.js";
import {
    AUTH_STATUSES,
    AUTH_TYPES,
    ICD_CODES,
    loadEmr,
    NECESSITY_MODES,
    NECESSITY_NOT_MET,
    NETWORK_KINDS,
    newId,
    PROVIDER_TYPES,
    RELATIONSHIPS,
    resetEmr,
    saveEmr,
    SEXES,
    UM_MISSING,
    UM_SOURCES,
    UM_WHEN,
} from "./emrData.js";
import { ageOn, agesOutOn, coverageTier, coveredOn, familyMembers, familyMet } from "./family.js";
import { findCriteria, icdLabel, normalizeIcd, reviewRequest } from "./medicalNecessity.js";
import "./EmrPanel.scss";

const TABS = [
    { id: "claim", label: "Claim" },
    { id: "members", label: "Members" },
    { id: "providers", label: "Providers" },
    { id: "networks", label: "Networks" },
    { id: "fees", label: "Fees" },
    { id: "um", label: "UM" },
];

const PLACES = [
    ["11", "Office"],
    ["02", "Telehealth, not at home"],
    ["10", "Telehealth, at home"],
    ["12", "Home"],
    ["19", "Hospital outpatient, off campus"],
    ["20", "Urgent care"],
    ["21", "Inpatient hospital"],
    ["22", "Hospital outpatient, on campus"],
    ["23", "Emergency room"],
    ["24", "Ambulatory surgery center"],
    ["41", "Ambulance, land"],
    ["42", "Ambulance, air"],
    ["81", "Independent lab"],
];

const BILL_TYPES = [
    ["0111", "Inpatient hospital"],
    ["0131", "Hospital outpatient"],
    ["0831", "Ambulatory surgery"],
    ["0711", "Rural health clinic"],
    ["0851", "Critical access hospital"],
];

const STATUS_WORDS = { pass: "Pass", note: "Note", warn: "Check", stop: "Stop", skip: "Skipped" };
const LINE_STATUS = { paid: "Paid", denied: "Denied", pended: "On hold" };

const today = () => new Date().toISOString().slice(0, 10);
const emptyLine = () => ({ code: "", modifiers: "", revenueCode: "", units: 1, billed: "" });
const accumulatorFields = (member) => ({
    deductibleMet: member ? String(member.deductibleMet || "") : "",
    outOfPocketMet: member ? String(member.outOfPocketMet || "") : "",
    oonDeductibleMet: member ? String(member.oonDeductibleMet || "") : "",
    oonOutOfPocketMet: member ? String(member.oonOutOfPocketMet || "") : "",
});

const emptyClaim = (providerId = "") => ({
    planIndex: 0,
    memberId: "",
    dateOfService: today(),
    diagnosisCodes: [],
    type: "professional",
    providerId,
    placeOfService: "11",
    typeOfBill: "0131",
    emergency: false,
    admitted: false,
    ...accumulatorFields(null),
    lines: [emptyLine()],
});

const EXAMPLES = [
    { id: "preventive", label: "Preventive: yearly checkup with a PCP", plan: "Partnership", claim: { diagnosisCodes: ["Z00.00"], memberId: "mem-jane", providerId: "prv-patel", lines: [{ code: "99396", billed: 250 }] } },
    { id: "preventiveSickSameDay", label: "Preventive: checkup plus a sick visit the same day", plan: "PPO", claim: { diagnosisCodes: ["Z00.00", "E11.9"], memberId: "mem-sam", providerId: "prv-patel", lines: [{ code: "99396", billed: 250 }, { code: "99213", modifiers: "25", billed: 180 }] } },
    { id: "preventiveSpecialist", label: "Preventive: checkup billed by a specialist", plan: "PPO", claim: { diagnosisCodes: ["Z00.00"], memberId: "mem-sam", providerId: "prv-chen", lines: [{ code: "99396", billed: 250 }] } },
    { id: "preventiveMammogram", label: "Preventive: screening mammogram", plan: "PPO", claim: { diagnosisCodes: ["Z12.31"], memberId: "mem-sam", providerId: "prv-clearview", lines: [{ code: "77067", billed: 320 }] } },
    { id: "pcpLabs", label: "PCP visit with labs", plan: "Partnership", claim: { diagnosisCodes: ["E11.9"], memberId: "mem-jane", providerId: "prv-patel", lines: [{ code: "99213", billed: 180 }, { code: "80053", billed: 40 }, { code: "36415", billed: 12 }] } },
    { id: "notTheirPcp", label: "PCP visit with a doctor who isn't the member's PCP", plan: "Partnership", claim: { diagnosisCodes: ["J18.9"], memberId: "mem-jane", providerId: "prv-okafor", lines: [{ code: "99213", billed: 180 }] } },
    { id: "specialistReferral", label: "Specialist with a referral", plan: "Partnership", claim: { diagnosisCodes: ["R07.9"], memberId: "mem-jane", providerId: "prv-chen", lines: [{ code: "99214", billed: 260 }] } },
    { id: "specialistNoReferral", label: "Specialist with no referral", plan: "Partnership", claim: { diagnosisCodes: ["R07.9"], memberId: "mem-alex", providerId: "prv-chen", lines: [{ code: "99214", billed: 260 }] } },
    { id: "termed", label: "Member whose coverage ended", plan: "PPO", claim: { diagnosisCodes: ["Z34.00"], memberId: "mem-pat", providerId: "prv-ruiz", lines: [{ code: "99213", billed: 200 }] } },
    { id: "mriApproved", label: "MRI with an approval", plan: "Traditional", claim: { diagnosisCodes: ["M54.16"], memberId: "mem-ria", providerId: "prv-clearview", lines: [{ code: "72148", billed: 1200 }] } },
    { id: "mriDenied", label: "MRI with a denied approval", plan: "Partnership", claim: { diagnosisCodes: ["M54.50"], memberId: "mem-jane", providerId: "prv-clearview", lines: [{ code: "72148", billed: 1200 }] } },
    { id: "mriWrongDiagnosis", label: "MRI with a diagnosis that doesn't support it", plan: "Traditional", claim: { diagnosisCodes: ["E11.9"], memberId: "mem-ria", providerId: "prv-clearview", lines: [{ code: "72148", billed: 1200 }] } },
    { id: "familyDeductible", label: "Family: deductible almost met by the family", plan: "PPO", claim: { diagnosisCodes: ["S83.241A"], memberId: "mem-noah", providerId: "prv-peak", lines: [{ code: "97110", units: 2, billed: 240 }] } },
    { id: "familyOutOfPocket", label: "Family: out-of-pocket maximum reached by the family", plan: "EPO", claim: { diagnosisCodes: ["R10.9"], type: "institutional", emergency: true, memberId: "mem-omar", providerId: "prv-mercy", typeOfBill: "0131", lines: [{ revenueCode: "0450", code: "99284", billed: 2400 }] } },
    { id: "agedOut", label: "Family: child who aged out at 26", plan: "Partnership", claim: { diagnosisCodes: ["J18.9"], memberId: "mem-max", providerId: "prv-patel", lines: [{ code: "99213", billed: 180 }] } },
    { id: "therapyLimit", label: "Therapy near the visit limit", plan: "PPO", claim: { diagnosisCodes: ["M54.50"], memberId: "mem-sam", providerId: "prv-peak", lines: [{ code: "97110", units: 3, billed: 180 }] } },
    { id: "inpatient", label: "Hospital stay, 3 days with 2 approved", plan: "Traditional", claim: { diagnosisCodes: ["I20.9"], type: "institutional", memberId: "mem-ria", providerId: "prv-mercy", typeOfBill: "0111", lines: [{ revenueCode: "0120", units: 3, billed: 12000 }] } },
    { id: "singleCase", label: "Out-of-network surgery with a single case agreement", plan: "Traditional", claim: { diagnosisCodes: ["S83.241A"], type: "institutional", memberId: "mem-ria", providerId: "prv-lakeside", typeOfBill: "0831", lines: [{ revenueCode: "0360", code: "29881", billed: 9000 }] } },
    { id: "erNotEmergency", label: "ER visit that was not an emergency", plan: "PPO", claim: { diagnosisCodes: ["N39.0"], type: "institutional", memberId: "mem-sam", providerId: "prv-mercy", typeOfBill: "0131", lines: [{ revenueCode: "0450", code: "99283", billed: 2400 }] } },
    { id: "ambulance", label: "Non-emergency ambulance with no approval", plan: "Traditional", claim: { diagnosisCodes: ["Z74.01"], memberId: "mem-ria", providerId: "prv-metro", placeOfService: "41", lines: [{ code: "A0428", billed: 900 }] } },
    { id: "oonChiro", label: "Out-of-network chiropractor", plan: "Traditional", claim: { diagnosisCodes: ["M54.50"], memberId: "mem-ria", providerId: "prv-sunrise", lines: [{ code: "98941", billed: 95 }] } },
];

const money = (value) => (value == null ? "" : `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const digits = (value) => String(value).replace(/[^\d.]/g, "");

const relationshipLabel = (value) => RELATIONSHIPS.find((item) => item.value === value)?.label || value;

const FamilySummary = ({ data, member, plan, date }) => {
    const family = member && (data.families || []).find((item) => item.id === member.familyId);
    if (!family) return null;
    const people = familyMembers(data, family.id);
    const others = familyMet(data, member, date, "inNetwork");
    const side = plan?.inNetwork || {};
    const shared = others.others.length > 0;
    return (
        <p className="emr__muted emr__family">
            <b>{family.name} · {family.familyNumber}</b> · {coverageTier(people)} · {people.length} {people.length === 1 ? "person" : "people"}.
            {!shared ? " Only the individual amounts apply." : null}
            {shared && Number(side.deductible?.family) ? ` The rest of the family has put ${money(others.deductible)} toward the ${money(side.deductible.family)} family deductible.` : null}
            {shared && Number(side.outOfPocketMaximum?.family) ? ` They have put ${money(others.outOfPocket)} toward the ${money(side.outOfPocketMaximum.family)} family out-of-pocket maximum.` : null}
        </p>
    );
};

const Field = ({ label, children, wide }) => (
    <label className={`emr__field${wide ? " is-wide" : ""}`}>
        <span>{label}</span>
        {children}
    </label>
);

const Choice = ({ value, onChange, options, empty }) => (
    <select value={value ?? ""} onChange={(event) => onChange(event.target.value)}>
        {empty != null ? <option value="">{empty}</option> : null}
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
    </select>
);

const useServiceOptions = () => useMemo(
    () => loadServices().map((service) => ({ value: service.key, label: serviceName(service.key) })).sort((left, right) => left.label.localeCompare(right.label)),
    [],
);

const providerSelectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 80 }),
    control: (base, state) => ({
        ...base,
        minHeight: "2rem",
        borderColor: state.isFocused ? "var(--skyDark, #307da1)" : "var(--lighterGrey, #e0e0e0)",
        borderRadius: "0.35rem",
        boxShadow: "none",
        "&:hover": { borderColor: "var(--skyDark, #307da1)" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 0.5rem" }),
    menu: (base) => ({ ...base, marginTop: 4, border: "1px solid var(--lighterGrey, #e0e0e0)", borderRadius: "0.4rem", boxShadow: "0 6px 18px rgba(0, 0, 0, 0.08)", overflow: "hidden" }),
    menuList: (base) => ({ ...base, padding: 4, maxHeight: 360 }),
    groupHeading: (base) => ({ ...base, margin: "0.35rem 0 0.2rem", color: "#666", fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.06em" }),
    option: (base, state) => ({
        ...base,
        padding: "0.45rem 0.6rem",
        borderRadius: "0.3rem",
        whiteSpace: "normal",
        color: "var(--darkerGrey, #333)",
        backgroundColor: state.isSelected ? "#eaf3f8" : state.isFocused ? "#f7f7f7" : "#fff",
        cursor: "pointer",
    }),
};

const diagnosisSelectStyles = {
    ...providerSelectStyles,
    valueContainer: (base) => ({ ...base, padding: "2px 0.35rem", gap: 4 }),
    multiValue: (base) => ({ ...base, margin: 0, borderRadius: "0.3rem", backgroundColor: "#eaf3f8" }),
    multiValueLabel: (base) => ({ ...base, fontSize: "0.78rem", fontWeight: 600, color: "var(--darkerGrey, #333)" }),
};

const DiagnosisSelect = ({ value = [], onChange, placeholder = "Search ICD-10 by code or words" }) => {
    const options = ICD_CODES.map((item) => ({ value: item.code, label: item.code, description: item.description }));
    const selected = value.map((code) => options.find((option) => option.value === code) || { value: code, label: code, description: "Not in the sample list" });
    return (
        <CreatableSelect
            isMulti
            className="react-select-container"
            classNamePrefix="react-select"
            options={options}
            value={selected}
            onChange={(items) => onChange((items || []).map((item) => normalizeIcd(item.value)))}
            filterOption={(option, input) => {
                const needle = input.trim().toLowerCase();
                return !needle || option.value.toLowerCase().includes(needle) || String(option.data.description || "").toLowerCase().includes(needle);
            }}
            isValidNewOption={(input) => /^[A-Za-z]\d[\dA-Za-z]\.?[\dA-Za-z]{0,4}$/.test(input.trim()) && !options.some((option) => option.value === normalizeIcd(input))}
            formatCreateLabel={(input) => `Use ${normalizeIcd(input)}`}
            formatOptionLabel={(option, { context }) => (
                context === "menu" && option.description ? (
                    <span className="emr-option">
                        <span className="emr-option__title"><b>{option.label}</b></span>
                        <small>{option.description}</small>
                    </span>
                ) : option.label
            )}
            placeholder={placeholder}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            styles={diagnosisSelectStyles}
        />
    );
};

const ProviderSelect = ({ providers, value, plan, data, onChange }) => {
    const networkName = (id) => data.networks.find((network) => network.id === id)?.name;
    const toOption = (provider) => {
        const status = networkStatus(plan, provider, data);
        const networks = provider.networkIds.map(networkName).filter(Boolean);
        const ownRates = data.feeSchedules
            .filter((schedule) => schedule.providerId === provider.id)
            .map((schedule) => networkName(schedule.networkId))
            .filter(Boolean);
        return {
            value: provider.id,
            provider,
            inNetwork: status.inNetwork,
            via: status.network?.name || "",
            networks,
            ownRates,
            search: [provider.name, provider.npi, provider.taxonomy, provider.specialty, ...networks].join(" ").toLowerCase(),
        };
    };
    const options = providers.map(toOption);
    const groups = [
        { label: `In network for ${plan?.planName || "this plan"}`, options: options.filter((option) => option.inNetwork) },
        { label: "Out of network", options: options.filter((option) => !option.inNetwork) },
    ].filter((group) => group.options.length);
    return (
        <Select
            inputId="emr-provider"
            aria-label="Provider"
            className="react-select-container"
            classNamePrefix="react-select"
            options={groups}
            value={options.find((option) => option.value === value) || null}
            onChange={(option) => onChange(option?.value || "")}
            filterOption={(option, input) => !input || option.data.search.includes(input.trim().toLowerCase())}
            formatOptionLabel={(option, { context }) => (
                context === "menu" ? (
                    <span className="emr-option">
                        <span className="emr-option__title">
                            <b>{option.provider.name}</b>
                            <em className={option.inNetwork ? "is-in" : "is-out"}>{option.inNetwork ? `In network · ${option.via}` : "Out of network"}</em>
                        </span>
                        <small>{[option.provider.specialty, `Taxonomy ${option.provider.taxonomy}`, `NPI ${option.provider.npi}`].filter(Boolean).join(" · ")}</small>
                        <small>
                            {option.networks.length ? `Networks: ${option.networks.join(", ")}` : "No networks"}
                            {option.ownRates.length ? ` · Own rates on ${option.ownRates.join(", ")}` : ""}
                        </small>
                    </span>
                ) : `${option.provider.name} · ${option.provider.specialty || option.provider.taxonomy}`
            )}
            placeholder="Search by name, NPI, taxonomy, or network"
            noOptionsMessage={() => "No providers match"}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            styles={providerSelectStyles}
        />
    );
};

const ClaimTab = ({ account, data }) => {
    const plans = account?.benefitPlans || [];
    const [claim, setClaim] = useState(() => emptyClaim(data.providers[0]?.id || ""));
    const [result, setResult] = useState(null);
    const [stale, setStale] = useState(false);
    const plan = plans[claim.planIndex] || null;
    const members = (data.members || []).filter((member) => plan && member.planId === plan.planId);
    const providers = data.providers.filter((provider) => provider.type === claim.type);
    const provider = data.providers.find((item) => item.id === claim.providerId) || null;
    const status = networkStatus(plan, provider, data);

    const change = (patch) => {
        setClaim((current) => ({ ...current, ...patch }));
        if (result) setStale(true);
    };
    const setLine = (index, field, value) => change({ lines: claim.lines.map((line, lineIndex) => (lineIndex === index ? { ...line, [field]: value } : line)) });
    const setType = (type) => {
        const first = data.providers.find((item) => item.type === type);
        change({ type, providerId: provider?.type === type ? provider.id : first?.id || "" });
    };
    const claimMember = data.members.find((member) => member.id === claim.memberId);
    const setMember = (memberId) => change({ memberId, ...accumulatorFields(data.members.find((member) => member.id === memberId)) });
    const setPlan = (planIndex) => change({ planIndex, memberId: "", ...accumulatorFields(null) });
    const loadExample = (id) => {
        const example = EXAMPLES.find((item) => item.id === id);
        if (!example) return;
        const planIndex = plans.findIndex((item) => item.planName === example.plan);
        const nextPlan = plans[planIndex >= 0 ? planIndex : claim.planIndex];
        const member = data.members.find((item) => item.id === example.claim.memberId && item.planId === nextPlan?.planId);
        setClaim({
            ...emptyClaim(),
            dateOfService: "2026-05-15",
            ...example.claim,
            planIndex: planIndex >= 0 ? planIndex : claim.planIndex,
            memberId: member?.id || "",
            ...accumulatorFields(member),
            lines: example.claim.lines.map((line) => ({ ...emptyLine(), ...line })),
        });
        setResult(null);
        setStale(false);
    };
    const run = () => {
        const next = adjudicateClaim({
            claim: { ...claim, lines: claim.lines.filter((line) => line.code || line.revenueCode) },
            plan,
            catalog: loadServices(),
            data,
        });
        console.log("Mock claim adjudication", next);
        setResult(next);
        setStale(false);
    };

    if (!plans.length) return <p className="emr__muted">This account has no benefit plans yet.</p>;

    return (
        <div className="emr__stack">
            <Field label="Start from an example" wide>
                <select value="" onChange={(event) => loadExample(event.target.value)}>
                    <option value="">Pick an example</option>
                    {EXAMPLES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                </select>
            </Field>
            <div className="emr__grid">
                <Field label="Plan">
                    <select value={claim.planIndex} onChange={(event) => setPlan(Number(event.target.value))}>
                        {plans.map((item, index) => <option key={`${item.planName}-${index}`} value={index}>{item.planName || `Plan ${index + 1}`}</option>)}
                    </select>
                </Field>
                <Field label="Member">
                    <Choice
                        value={claim.memberId}
                        onChange={setMember}
                        empty={members.length ? "No member" : "No members on this plan"}
                        options={members.map((member) => ({ value: member.id, label: `${member.name} · ${member.memberNumber}${member.relationship ? ` · ${relationshipLabel(member.relationship)}` : ""}` }))}
                    />
                </Field>
                <Field label="Date of service">
                    <input type="date" value={claim.dateOfService} onChange={(event) => change({ dateOfService: event.target.value })} />
                </Field>
                {claim.type === "professional" ? (
                    <Field label="Place of service">
                        <select value={claim.placeOfService} onChange={(event) => change({ placeOfService: event.target.value })}>
                            {PLACES.map(([value, label]) => <option key={value} value={value}>{value} {label}</option>)}
                        </select>
                    </Field>
                ) : (
                    <Field label="Type of bill">
                        <select value={claim.typeOfBill} onChange={(event) => change({ typeOfBill: event.target.value })}>
                            {BILL_TYPES.map(([value, label]) => <option key={value} value={value}>{value} {label}</option>)}
                        </select>
                    </Field>
                )}
            </div>

            <div className="emr__field is-wide">
                <span>Diagnosis codes (ICD-10)</span>
                <DiagnosisSelect value={claim.diagnosisCodes} onChange={(diagnosisCodes) => change({ diagnosisCodes })} />
            </div>

            <div className="emr__segment" role="group" aria-label="Claim type">
                {PROVIDER_TYPES.map((type) => (
                    <button key={type.value} type="button" aria-pressed={claim.type === type.value} className={claim.type === type.value ? "is-on" : undefined} onClick={() => setType(type.value)}>
                        {type.value === "professional" ? "Professional (CMS-1500)" : "Institutional (UB-04)"}
                    </button>
                ))}
            </div>

            <div className="emr__field is-wide">
                <span>Provider</span>
                <ProviderSelect providers={providers} value={claim.providerId} plan={plan} data={data} onChange={(providerId) => change({ providerId })} />
            </div>
            {provider ? (
                <p className={`emr__badge ${status.inNetwork ? "is-in" : "is-out"}`}>
                    {status.inNetwork ? `In network for ${plan?.planName} through ${status.network.name}` : `Out of network for ${plan?.planName}`}
                    <small>Taxonomy {provider.taxonomy} · NPI {provider.npi}</small>
                    {claimMember?.pcpProviderId ? (
                        <small>
                            {claimMember.pcpProviderId === provider.id
                                ? `${provider.name} is ${claimMember.name}'s PCP.`
                                : `${claimMember.name}'s PCP is ${data.providers.find((item) => item.id === claimMember.pcpProviderId)?.name || "not in the provider list"}.`}
                        </small>
                    ) : null}
                </p>
            ) : <p className="emr__muted">Add a {claim.type} provider on the Providers tab.</p>}

            <div className="emr__checks">
                {[
                    ["emergency", "It was an emergency"],
                    ["admitted", "Admitted from the ER"],
                ].map(([field, label]) => (
                    <label key={field}>
                        <input type="checkbox" checked={Boolean(claim[field])} onChange={(event) => change({ [field]: event.target.checked })} />
                        {label}
                    </label>
                ))}
            </div>

            <div>
                <h4 className="emr__head">Member so far this year</h4>
                <div className="emr__grid">
                    {[
                        ["deductibleMet", "In-network deductible met"],
                        ["outOfPocketMet", "In-network out-of-pocket met"],
                        ["oonDeductibleMet", "Out-of-network deductible met"],
                        ["oonOutOfPocketMet", "Out-of-network out-of-pocket met"],
                    ].map(([field, label]) => (
                        <Field key={field} label={label}>
                            <input inputMode="decimal" placeholder="$0" value={claim[field]} onChange={(event) => change({ [field]: digits(event.target.value) })} />
                        </Field>
                    ))}
                </div>
                <FamilySummary data={data} member={data.members.find((item) => item.id === claim.memberId)} plan={plan} date={claim.dateOfService} />
            </div>

            <div>
                <h4 className="emr__head">Lines</h4>
                <div className={`emr__lines${claim.type === "institutional" ? " is-institutional" : ""}`}>
                    <div className="emr__line is-head">
                        {claim.type === "institutional" ? <span>Revenue</span> : null}
                        <span>{claim.type === "institutional" ? "HCPCS / CPT" : "CPT / HCPCS"}</span>
                        <span>Modifiers</span>
                        <span>Units</span>
                        <span>Billed</span>
                        <span />
                    </div>
                    {claim.lines.map((line, index) => (
                        <div className="emr__line" key={index}>
                            {claim.type === "institutional" ? (
                                <input aria-label={`Line ${index + 1} revenue code`} placeholder="0450" value={line.revenueCode} onChange={(event) => setLine(index, "revenueCode", event.target.value)} />
                            ) : null}
                            <input aria-label={`Line ${index + 1} code`} placeholder="99213" value={line.code} onChange={(event) => setLine(index, "code", event.target.value)} />
                            <input aria-label={`Line ${index + 1} modifiers`} value={line.modifiers} onChange={(event) => setLine(index, "modifiers", event.target.value)} />
                            <input aria-label={`Line ${index + 1} units`} inputMode="numeric" value={line.units} onChange={(event) => setLine(index, "units", event.target.value.replace(/\D/g, ""))} />
                            <input aria-label={`Line ${index + 1} billed`} inputMode="decimal" placeholder="$0" value={line.billed} onChange={(event) => setLine(index, "billed", digits(event.target.value))} />
                            <button type="button" className="emr__icon" aria-label={`Remove line ${index + 1}`} onClick={() => change({ lines: claim.lines.filter((_, lineIndex) => lineIndex !== index) })}>
                                <LuTrash2 />
                            </button>
                        </div>
                    ))}
                    <button type="button" className="emr__link" onClick={() => change({ lines: [...claim.lines, emptyLine()] })}>
                        <LuPlus /> Add line
                    </button>
                </div>
            </div>

            <button type="button" className="emr__primary" disabled={!provider || !claim.lines.some((line) => line.code || line.revenueCode)} onClick={run}>
                Adjudicate claim
            </button>

            {result ? <ClaimResult result={result} stale={stale} /> : null}
        </div>
    );
};

const StepList = ({ steps, onlyChanges }) => {
    const shown = onlyChanges ? steps.filter((step) => !["pass", "skip"].includes(step.status)) : steps;
    if (!shown.length) return <p className="emr__muted">Every step passed.</p>;
    return (
        <ol className="emr__steps">
            {shown.map((step) => (
                <li key={step.id} className={`is-${step.status}`}>
                    <span className="emr__step-number">{step.id}</span>
                    <div>
                        <p>
                            <b>{step.name}</b>
                            <em>{STATUS_WORDS[step.status]}</em>
                        </p>
                        <small>{step.detail}</small>
                    </div>
                </li>
            ))}
        </ol>
    );
};

const ClaimResult = ({ result, stale }) => {
    const [onlyChanges, setOnlyChanges] = useState(false);
    const [closed, setClosed] = useState(() => new Set());
    const toggle = (index) => setClosed((current) => {
        const next = new Set(current);
        if (next.has(index)) next.delete(index);
        else next.add(index);
        return next;
    });
    return (
        <section className={`emr__result${stale ? " is-stale" : ""}`} aria-live="polite">
            {stale ? <p className="emr__muted">The claim changed. Adjudicate again to update these numbers.</p> : null}
            <div className="emr__totals">
                <div><span>Billed</span><b>{money(result.totals.billed)}</b></div>
                <div><span>Allowed</span><b>{money(result.totals.allowed)}</b></div>
                <div><span>Plan pays</span><b>{money(result.totals.planPays)}</b></div>
                <div className="is-member"><span>Member pays</span><b>{money(result.totals.memberPays)}</b></div>
            </div>
            {result.totals.balanceBill ? (
                <p className="emr__warn">The out-of-network provider may also bill the member up to {money(result.totals.balanceBill)}.</p>
            ) : null}
            {result.totals.providerWriteOff ? (
                <p className="emr__muted">The provider writes off {money(result.totals.providerWriteOff)} for missing approvals.</p>
            ) : null}

            <label className="emr__toggle">
                <input type="checkbox" checked={onlyChanges} onChange={(event) => setOnlyChanges(event.target.checked)} />
                Only show steps that changed something
            </label>

            <div className="emr__phase">
                <h4 className="emr__head">Claim intake</h4>
                <StepList steps={result.steps} onlyChanges={onlyChanges} />
                {result.status !== "pended" || result.lines.length ? <p className="emr__lock">Claim data locked</p> : null}
            </div>

            {result.lines.map((line) => (
                <article className={`emr__card is-${line.status}`} key={line.index}>
                    <header>
                        <b>Line {line.index + 1} · {[line.revenueCode, line.code].filter(Boolean).join(" · ")}{line.modifiers ? ` (${line.modifiers})` : ""}</b>
                        <span>{line.benefit || line.service || "No match"}</span>
                        <em>{LINE_STATUS[line.status]}</em>
                    </header>
                    <dl>
                        <div><dt>Billed</dt><dd>{money(line.billed)}</dd></div>
                        <div><dt>Allowed</dt><dd>{money(line.allowed)}</dd></div>
                        <div><dt>Plan pays</dt><dd>{money(line.planPays)}</dd></div>
                        <div className="is-member"><dt>Member pays</dt><dd>{money(line.memberPays)}</dd></div>
                    </dl>
                    <button type="button" className="emr__link" aria-expanded={!closed.has(line.index)} onClick={() => toggle(line.index)}>
                        {closed.has(line.index) ? "Show steps 5 to 26" : "Hide steps"}
                    </button>
                    {closed.has(line.index) ? null : <StepList steps={line.steps} onlyChanges={onlyChanges} />}
                </article>
            ))}

            {result.closingSteps.length ? (
                <div className="emr__phase">
                    <h4 className="emr__head">Payment</h4>
                    <StepList steps={result.closingSteps} onlyChanges={false} />
                    <p className="emr__lock">Claim data unlocked</p>
                </div>
            ) : null}

            {Object.entries(result.accumulators || {}).map(([side, value]) => (
                <p className="emr__muted" key={side}>
                    {side === "inNetwork" ? "In network" : "Out of network"}: {money(value.deductibleApplied)} went to the deductible ({money(value.deductibleLeft)} left of {money(value.deductible)}).
                    {" "}{money(value.outOfPocketApplied)} went to the out-of-pocket maximum{value.outOfPocketLeft == null ? "." : ` (${money(value.outOfPocketLeft)} left of ${money(value.outOfPocket)}).`}
                    {value.familyDeductible ? ` The family is now at ${money(value.familyDeductibleMet)} of its ${money(value.familyDeductible)} deductible.` : ""}
                    {value.familyOutOfPocket ? ` The family is now at ${money(value.familyOutOfPocketMet)} of its ${money(value.familyOutOfPocket)} out-of-pocket maximum.` : ""}
                </p>
            ))}
        </section>
    );
};

const Actions = ({ onCancel, onSave, disabled, label }) => (
    <div className="emr__actions">
        <button type="button" className="emr__secondary" onClick={onCancel}>Cancel</button>
        <button type="button" className="emr__primary" disabled={disabled} onClick={onSave}>{label}</button>
    </div>
);

const upsert = (list, item) => (list.some((entry) => entry.id === item.id) ? list.map((entry) => (entry.id === item.id ? item : entry)) : [...list, item]);

const MembersTab = ({ account, data, update }) => {
    const services = useServiceOptions();
    const [draft, setDraft] = useState(null);
    const [familyDraft, setFamilyDraft] = useState(null);
    const families = data.families || [];
    const members = data.members || [];
    const planOptions = (account?.benefitPlans || []).map((plan) => ({ value: plan.planId, label: `${plan.planName} (${plan.planId})` }));
    const planFor = (planId) => account?.benefitPlans?.find((plan) => plan.planId === planId);
    const planName = (planId) => planFor(planId)?.planName || planId || "No plan";
    const used = draft ? Object.entries(draft.unitsUsed || {}) : [];
    const setUsed = (entries) => setDraft({ ...draft, unitsUsed: Object.fromEntries(entries.filter(([key]) => key)) });
    const draftFamily = draft && families.find((family) => family.id === draft.familyId);
    const pcpOptions = data.providers
        .filter((provider) => provider.type === "professional" && PCP_TAXONOMY.includes(provider.taxonomy))
        .map((provider) => ({ value: provider.id, label: `${provider.name} · ${provider.specialty}` }));
    const pcpName = (id) => data.providers.find((provider) => provider.id === id)?.name;
    const blankMember = (family) => {
        const people = family ? familyMembers(data, family.id) : [];
        const base = people.find((person) => person.relationship === "subscriber")?.memberNumber;
        return {
            id: newId("mem"),
            memberNumber: base ? `${base}-${String(people.length + 1).padStart(2, "0")}` : "",
            name: "",
            familyId: family?.id || "",
            relationship: people.length ? "child" : "subscriber",
            dateOfBirth: "",
            sex: "U",
            pcpProviderId: people.find((person) => person.relationship === "subscriber")?.pcpProviderId || "",
            planId: family?.planId || planOptions[0]?.value || "",
            coverageFrom: "2026-01-01",
            coverageTo: "2026-12-31",
            ...Object.fromEntries(Object.keys(accumulatorFields(null)).map((field) => [field, 0])),
            unitsUsed: {},
        };
    };
    const save = () => {
        update({ ...data, members: upsert(members, { ...draft, name: draft.name.trim(), planId: draftFamily?.planId || draft.planId }) });
        setDraft(null);
    };
    const saveFamily = () => {
        const family = { ...familyDraft, name: familyDraft.name.trim() };
        update({
            ...data,
            families: upsert(families, family),
            members: members.map((member) => (member.familyId === family.id ? { ...member, planId: family.planId } : member)),
        });
        setFamilyDraft(null);
    };
    const removeFamily = (family) => update({
        ...data,
        families: families.filter((item) => item.id !== family.id),
        members: members.map((member) => (member.familyId === family.id ? { ...member, familyId: "" } : member)),
    });
    const order = { subscriber: 0, spouse: 1, domesticPartner: 1, child: 2 };
    const now = today();
    const personRow = (member) => {
        const age = ageOn(member.dateOfBirth, now);
        const limit = agesOutOn(member);
        return (
            <li key={member.id}>
                <div>
                    <b>{member.name} · {member.memberNumber}</b>
                    <small>
                        {member.relationship ? relationshipLabel(member.relationship) : "No relationship"}
                        {age != null ? ` · ${age} years old` : ""}
                        {member.sex ? ` · ${SEXES.find((item) => item.value === member.sex)?.label || member.sex}` : ""}
                        {limit ? (now <= limit ? ` · covered as a child through ${limit}` : ` · aged out on ${limit}`) : ""}
                    </small>
                    <small>{member.pcpProviderId ? `PCP ${pcpName(member.pcpProviderId) || "not in the provider list"}` : "No PCP picked"}</small>
                    <small>
                        {member.familyId ? "" : `${planName(member.planId)} · `}covered {member.coverageFrom} to {member.coverageTo} · deductible met {money(member.deductibleMet)} · out-of-pocket met {money(member.outOfPocketMet)}
                        {Object.keys(member.unitsUsed || {}).length ? ` · ${Object.entries(member.unitsUsed).map(([key, count]) => `${serviceName(key)} ${count}`).join(", ")}` : ""}
                    </small>
                </div>
                <button type="button" className="emr__link" onClick={() => { setFamilyDraft(null); setDraft(structuredClone(member)); }}>Edit</button>
                <button type="button" className="emr__icon" aria-label={`Delete ${member.name}`} onClick={() => update({ ...data, members: members.filter((item) => item.id !== member.id) })}>
                    <LuTrash2 />
                </button>
            </li>
        );
    };
    const loose = members.filter((member) => !families.some((family) => family.id === member.familyId));
    return (
        <div className="emr__stack">
            <div className="emr__toolbar">
                <p className="emr__muted">{families.length} families · {members.length} people</p>
                <span className="emr__buttons">
                    <button type="button" className="emr__link" onClick={() => { setDraft(null); setFamilyDraft({ id: newId("fam"), familyNumber: `F-${Math.floor(5000 + Math.random() * 4000)}`, name: "", planId: planOptions[0]?.value || "" }); }}>
                        <LuPlus /> Add family
                    </button>
                    <button type="button" className="emr__link" onClick={() => { setFamilyDraft(null); setDraft(blankMember(null)); }}>
                        <LuPlus /> Add member
                    </button>
                </span>
            </div>
            {familyDraft ? (
                <div className="emr__form">
                    <div className="emr__grid">
                        <Field label="Family name"><input value={familyDraft.name} placeholder="Doe family" onChange={(event) => setFamilyDraft({ ...familyDraft, name: event.target.value })} /></Field>
                        <Field label="Family ID"><input value={familyDraft.familyNumber} onChange={(event) => setFamilyDraft({ ...familyDraft, familyNumber: event.target.value })} /></Field>
                        <Field label="Plan" wide><Choice value={familyDraft.planId} onChange={(planId) => setFamilyDraft({ ...familyDraft, planId })} options={planOptions} /></Field>
                    </div>
                    <p className="emr__muted">Everyone in the family shares this plan. Changing it moves the whole family.</p>
                    <Actions onCancel={() => setFamilyDraft(null)} onSave={saveFamily} disabled={!familyDraft.name.trim() || !familyDraft.planId} label="Save family" />
                </div>
            ) : null}
            {draft ? (
                <div className="emr__form">
                    <div className="emr__grid">
                        <Field label="Name"><input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></Field>
                        <Field label="Member ID"><input value={draft.memberNumber} placeholder="M-1234" onChange={(event) => setDraft({ ...draft, memberNumber: event.target.value })} /></Field>
                        <Field label="Family">
                            <Choice
                                value={draft.familyId}
                                onChange={(familyId) => setDraft({ ...draft, familyId, planId: families.find((family) => family.id === familyId)?.planId || draft.planId })}
                                empty="No family"
                                options={families.map((family) => ({ value: family.id, label: `${family.name} · ${family.familyNumber}` }))}
                            />
                        </Field>
                        <Field label="Relationship"><Choice value={draft.relationship} onChange={(relationship) => setDraft({ ...draft, relationship })} options={RELATIONSHIPS} /></Field>
                        <Field label="Date of birth"><input type="date" value={draft.dateOfBirth} onChange={(event) => setDraft({ ...draft, dateOfBirth: event.target.value })} /></Field>
                        <Field label="Sex"><Choice value={draft.sex} onChange={(sex) => setDraft({ ...draft, sex })} options={SEXES} /></Field>
                        <Field label="Primary care provider (PCP)" wide>
                            <Choice
                                value={draft.pcpProviderId || ""}
                                onChange={(pcpProviderId) => setDraft({ ...draft, pcpProviderId })}
                                empty="No PCP picked"
                                options={pcpOptions}
                            />
                        </Field>
                        {draftFamily ? (
                            <Field label="Plan" wide><input value={`${planName(draftFamily.planId)}, from the ${draftFamily.name}`} readOnly /></Field>
                        ) : (
                            <Field label="Plan" wide><Choice value={draft.planId} onChange={(planId) => setDraft({ ...draft, planId })} options={planOptions} /></Field>
                        )}
                        <Field label="Covered from"><input type="date" value={draft.coverageFrom} onChange={(event) => setDraft({ ...draft, coverageFrom: event.target.value })} /></Field>
                        <Field label="Covered to"><input type="date" value={draft.coverageTo} onChange={(event) => setDraft({ ...draft, coverageTo: event.target.value })} /></Field>
                        {[
                            ["deductibleMet", "In-network deductible met"],
                            ["outOfPocketMet", "In-network out-of-pocket met"],
                            ["oonDeductibleMet", "Out-of-network deductible met"],
                            ["oonOutOfPocketMet", "Out-of-network out-of-pocket met"],
                        ].map(([field, label]) => (
                            <Field key={field} label={label}>
                                <input inputMode="decimal" value={draft[field]} onChange={(event) => setDraft({ ...draft, [field]: Number(digits(event.target.value)) || 0 })} />
                            </Field>
                        ))}
                    </div>
                    {draft.relationship === "child" && draft.dateOfBirth ? (
                        <p className="emr__muted">Covered as a child through {agesOutOn(draft)}, the end of the month they turn 26.</p>
                    ) : null}
                    <div>
                        <h4 className="emr__head">Visits used this year</h4>
                        <div className="emr__rates">
                            {used.map(([key, count], index) => (
                                <div className="emr__rate is-used" key={index}>
                                    <Choice value={key} onChange={(value) => setUsed(used.map((entry, entryIndex) => (entryIndex === index ? [value, entry[1]] : entry)))} options={services} />
                                    <input aria-label="Visits used" inputMode="numeric" value={count} onChange={(event) => setUsed(used.map((entry, entryIndex) => (entryIndex === index ? [entry[0], Number(event.target.value.replace(/\D/g, "")) || 0] : entry)))} />
                                    <button type="button" className="emr__icon" aria-label="Remove" onClick={() => setUsed(used.filter((_, entryIndex) => entryIndex !== index))}><LuTrash2 /></button>
                                </div>
                            ))}
                            <button type="button" className="emr__link" onClick={() => setUsed([...used, [services.find((option) => !draft.unitsUsed?.[option.value])?.value || "", 0]])}>
                                <LuPlus /> Add a count
                            </button>
                        </div>
                    </div>
                    <Actions onCancel={() => setDraft(null)} onSave={save} disabled={!draft.name.trim()} label="Save member" />
                </div>
            ) : null}
            {families.map((family) => {
                const people = familyMembers(data, family.id).sort((left, right) => (order[left.relationship] ?? 3) - (order[right.relationship] ?? 3) || String(left.dateOfBirth).localeCompare(String(right.dateOfBirth)));
                const side = planFor(family.planId)?.inNetwork || {};
                const met = (field) => people.filter((person) => coveredOn(person, now)).reduce((sum, person) => sum + Number(person[field] || 0), 0);
                return (
                    <section key={family.id} className="emr__family-card">
                        <header>
                            <div>
                                <b>{family.name} · {family.familyNumber}</b>
                                <small>{planName(family.planId)} · {coverageTier(people)} · {people.length} {people.length === 1 ? "person" : "people"}</small>
                                {people.length > 1 ? (
                                    <small>
                                        Family in network:
                                        {Number(side.deductible?.family) ? ` deductible met ${money(met("deductibleMet"))} of ${money(side.deductible.family)} ·` : ""}
                                        {` out-of-pocket met ${money(met("outOfPocketMet"))} of ${money(side.outOfPocketMaximum?.family)}`}
                                    </small>
                                ) : null}
                            </div>
                            <button type="button" className="emr__link" onClick={() => { setFamilyDraft(null); setDraft(blankMember(family)); }}><LuPlus /> Add person</button>
                            <button type="button" className="emr__link" onClick={() => { setDraft(null); setFamilyDraft(structuredClone(family)); }}>Edit</button>
                            <button type="button" className="emr__icon" aria-label={`Delete ${family.name}`} onClick={() => removeFamily(family)}>
                                <LuTrash2 />
                            </button>
                        </header>
                        <ul className="emr__list">{people.map(personRow)}</ul>
                    </section>
                );
            })}
            {loose.length ? (
                <section className="emr__family-card">
                    <header><div><b>Not in a family</b></div></header>
                    <ul className="emr__list">{loose.map(personRow)}</ul>
                </section>
            ) : null}
        </div>
    );
};

const ProvidersTab = ({ data, update }) => {
    const [draft, setDraft] = useState(null);
    const networks = data.networks.filter((network) => network.kind !== "reference");
    const save = () => {
        update({ ...data, providers: upsert(data.providers, { ...draft, name: draft.name.trim(), taxonomy: draft.taxonomy.trim().toUpperCase() }) });
        setDraft(null);
    };
    const toggleNetwork = (id) => setDraft((current) => ({
        ...current,
        networkIds: current.networkIds.includes(id) ? current.networkIds.filter((item) => item !== id) : [...current.networkIds, id],
    }));
    return (
        <div className="emr__stack">
            <div className="emr__toolbar">
                <p className="emr__muted">{data.providers.length} providers</p>
                <button type="button" className="emr__link" onClick={() => setDraft({ id: newId("prv"), name: "", npi: "", type: "professional", taxonomy: "", specialty: "", networkIds: [] })}>
                    <LuPlus /> Add provider
                </button>
            </div>
            {draft ? (
                <div className="emr__form">
                    <div className="emr__grid">
                        <Field label="Name"><input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></Field>
                        <Field label="NPI"><input value={draft.npi} inputMode="numeric" onChange={(event) => setDraft({ ...draft, npi: event.target.value.replace(/\D/g, "").slice(0, 10) })} /></Field>
                        <Field label="Type"><Choice value={draft.type} onChange={(type) => setDraft({ ...draft, type })} options={PROVIDER_TYPES} /></Field>
                        <Field label="Taxonomy code"><input value={draft.taxonomy} placeholder="207Q00000X" onChange={(event) => setDraft({ ...draft, taxonomy: event.target.value })} /></Field>
                        <Field label="Specialty" wide><input value={draft.specialty} placeholder="Family medicine" onChange={(event) => setDraft({ ...draft, specialty: event.target.value })} /></Field>
                    </div>
                    <div className="emr__checks">
                        <span className="emr__head">Networks</span>
                        {networks.map((network) => (
                            <label key={network.id}>
                                <input type="checkbox" checked={draft.networkIds.includes(network.id)} onChange={() => toggleNetwork(network.id)} />
                                {network.name}
                            </label>
                        ))}
                        {!draft.networkIds.length ? <small className="emr__muted">No networks means the provider is out of network for every plan.</small> : null}
                    </div>
                    <Actions onCancel={() => setDraft(null)} onSave={save} disabled={!draft.name.trim()} label="Save provider" />
                </div>
            ) : null}
            <ul className="emr__list">
                {data.providers.map((provider) => (
                    <li key={provider.id}>
                        <div>
                            <b>{provider.name}</b>
                            <small>{provider.type === "professional" ? "Professional" : "Institutional"} · {provider.specialty || provider.taxonomy} · {provider.taxonomy}</small>
                            <small>
                                {provider.networkIds.length
                                    ? provider.networkIds.map((id) => data.networks.find((network) => network.id === id)?.name).filter(Boolean).join(", ")
                                    : "No networks"}
                            </small>
                        </div>
                        <button type="button" className="emr__link" onClick={() => setDraft(structuredClone(provider))}>Edit</button>
                        <button type="button" className="emr__icon" aria-label={`Delete ${provider.name}`} onClick={() => update({ ...data, providers: data.providers.filter((item) => item.id !== provider.id) })}>
                            <LuTrash2 />
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const NetworksTab = ({ data, update, account }) => {
    const [draft, setDraft] = useState(null);
    const planNames = (name) => (account?.benefitPlans || [])
        .filter((plan) => (plan.networks || []).some((network) => String(network.name || "").trim().toLowerCase() === name.trim().toLowerCase()))
        .map((plan) => plan.planName);
    const save = () => {
        update({ ...data, networks: upsert(data.networks, { ...draft, name: draft.name.trim() }) });
        setDraft(null);
    };
    const remove = (id) => update({
        ...data,
        networks: data.networks.filter((network) => network.id !== id),
        providers: data.providers.map((provider) => ({ ...provider, networkIds: provider.networkIds.filter((item) => item !== id) })),
        feeSchedules: data.feeSchedules.filter((schedule) => schedule.networkId !== id),
        umRules: (data.umRules || []).filter((rule) => rule.networkId !== id),
    });
    return (
        <div className="emr__stack">
            <p className="emr__muted">A provider is in network when one of its networks has the same name as a network on the plan.</p>
            <div className="emr__toolbar">
                <p className="emr__muted">{data.networks.length} networks</p>
                <button type="button" className="emr__link" onClick={() => setDraft({ id: newId("net"), name: "", kind: "network" })}>
                    <LuPlus /> Add network
                </button>
            </div>
            {draft ? (
                <div className="emr__form">
                    <div className="emr__grid">
                        <Field label="Name"><input value={draft.name} placeholder="Centivo partnership" onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></Field>
                        <Field label="Kind"><Choice value={draft.kind} onChange={(kind) => setDraft({ ...draft, kind })} options={NETWORK_KINDS} /></Field>
                    </div>
                    <Actions onCancel={() => setDraft(null)} onSave={save} disabled={!draft.name.trim()} label="Save network" />
                </div>
            ) : null}
            <ul className="emr__list">
                {data.networks.map((network) => {
                    const plans = planNames(network.name);
                    const count = data.providers.filter((provider) => provider.networkIds.includes(network.id)).length;
                    return (
                        <li key={network.id}>
                            <div>
                                <b>{network.name}</b>
                                <small>{network.kind === "reference" ? "Prices for out-of-network claims" : `${count} provider${count === 1 ? "" : "s"}`}</small>
                                {network.kind === "reference" ? null : <small>{plans.length ? `Used by ${plans.join(", ")}` : "No plan on this account uses it"}</small>}
                            </div>
                            <button type="button" className="emr__link" onClick={() => setDraft({ ...network })}>Edit</button>
                            <button type="button" className="emr__icon" aria-label={`Delete ${network.name}`} onClick={() => remove(network.id)}>
                                <LuTrash2 />
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

const FeesTab = ({ data, update }) => {
    const [scheduleId, setScheduleId] = useState(data.feeSchedules[0]?.id || "");
    const [query, setQuery] = useState("");
    const [draft, setDraft] = useState(null);
    const schedule = data.feeSchedules.find((item) => item.id === scheduleId) || null;
    const networkName = (id) => data.networks.find((network) => network.id === id)?.name || "No network";
    const providerName = (id) => data.providers.find((provider) => provider.id === id)?.name || "";
    const scheduleLabel = (item) => `${item.name} · ${networkName(item.networkId)}${item.providerId ? ` · ${providerName(item.providerId)} only` : ""}`;
    const writeSchedule = (next) => update({ ...data, feeSchedules: data.feeSchedules.map((item) => (item.id === next.id ? next : item)) });
    const setRate = (index, field, value) => writeSchedule({
        ...schedule,
        lines: schedule.lines.map((line, lineIndex) => (lineIndex === index ? { ...line, [field]: value } : line)),
    });
    const shown = useMemo(() => {
        if (!schedule) return [];
        const needle = query.trim().toUpperCase();
        return schedule.lines.map((line, index) => ({ line, index })).filter(({ line }) => !needle || !line.code || String(line.code).toUpperCase().includes(needle));
    }, [schedule, query]);
    const create = () => {
        const next = { ...draft, name: draft.name.trim(), excludedCodes: [], lines: [] };
        update({ ...data, feeSchedules: [...data.feeSchedules, next] });
        setScheduleId(next.id);
        setDraft(null);
    };
    return (
        <div className="emr__stack">
            <p className="emr__muted">A network schedule prices everyone in the network. A schedule for one provider is its subcontract, and its prices go first.</p>
            <div className="emr__toolbar">
                <Field label="Fee schedule" wide>
                    <select value={scheduleId} onChange={(event) => setScheduleId(event.target.value)}>
                        {data.feeSchedules.map((item) => <option key={item.id} value={item.id}>{scheduleLabel(item)}</option>)}
                    </select>
                </Field>
                <button type="button" className="emr__link" onClick={() => setDraft({ id: newId("fs"), name: "", networkId: data.networks[0]?.id || "", providerId: "", fallbackPercentOfBilled: 50 })}>
                    <LuPlus /> Add fee schedule
                </button>
            </div>
            {draft ? (
                <div className="emr__form">
                    <div className="emr__grid">
                        <Field label="Name"><input value={draft.name} placeholder="Cigna 2027" onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></Field>
                        <Field label="Network"><Choice value={draft.networkId} onChange={(networkId) => setDraft({ ...draft, networkId })} options={data.networks.map((network) => ({ value: network.id, label: network.name }))} /></Field>
                        <Field label="Only for this provider" wide>
                            <Choice value={draft.providerId} onChange={(providerId) => setDraft({ ...draft, providerId })} empty="Everyone in the network" options={data.providers.map((provider) => ({ value: provider.id, label: provider.name }))} />
                        </Field>
                    </div>
                    <Actions onCancel={() => setDraft(null)} onSave={create} disabled={!draft.name.trim() || !draft.networkId} label="Create fee schedule" />
                </div>
            ) : null}
            {schedule ? (
                <>
                    <div className="emr__grid">
                        <Field label="Schedule name"><input value={schedule.name} onChange={(event) => writeSchedule({ ...schedule, name: event.target.value })} /></Field>
                        <Field label="Codes with no price pay this % of billed">
                            <input inputMode="decimal" value={schedule.fallbackPercentOfBilled} onChange={(event) => writeSchedule({ ...schedule, fallbackPercentOfBilled: Number(digits(event.target.value)) || 0 })} />
                        </Field>
                        <Field label="Codes the contract won't pay" wide>
                            <input
                                key={`${schedule.id}-${(schedule.excludedCodes || []).join(",")}`}
                                defaultValue={(schedule.excludedCodes || []).join(", ")}
                                placeholder="97811, S9083"
                                onBlur={(event) => writeSchedule({ ...schedule, excludedCodes: event.target.value.split(/[\s,]+/).map((code) => code.trim().toUpperCase()).filter(Boolean) })}
                            />
                        </Field>
                    </div>
                    <div className="emr__toolbar">
                        <input className="emr__search" type="search" placeholder="Find a code" value={query} onChange={(event) => setQuery(event.target.value)} aria-label="Find a code" />
                        <button type="button" className="emr__link" onClick={() => writeSchedule({ ...schedule, lines: [{ code: "", amount: 0 }, ...schedule.lines] })}>
                            <LuPlus /> Add price
                        </button>
                    </div>
                    <div className="emr__rates">
                        <div className="emr__rate is-head"><span>Code</span><span>Modifier</span><span>Price per unit</span><span /></div>
                        {shown.map(({ line, index }) => (
                            <div className="emr__rate" key={`${schedule.id}-${index}`}>
                                <input aria-label="Code" placeholder="99213 or 0450" value={line.code} onChange={(event) => setRate(index, "code", event.target.value.toUpperCase())} />
                                <input aria-label="Modifier" value={line.modifier || ""} onChange={(event) => setRate(index, "modifier", event.target.value.toUpperCase())} />
                                <input aria-label="Price per unit" inputMode="decimal" value={line.amount} onChange={(event) => setRate(index, "amount", Number(digits(event.target.value)) || 0)} />
                                <button type="button" className="emr__icon" aria-label={`Remove ${line.code || "price"}`} onClick={() => writeSchedule({ ...schedule, lines: schedule.lines.filter((_, lineIndex) => lineIndex !== index) })}>
                                    <LuTrash2 />
                                </button>
                            </div>
                        ))}
                        {!shown.length ? <p className="emr__muted">No prices match.</p> : null}
                    </div>
                    <button
                        type="button"
                        className="emr__danger"
                        onClick={() => {
                            update({ ...data, feeSchedules: data.feeSchedules.filter((item) => item.id !== schedule.id) });
                            setScheduleId(data.feeSchedules.find((item) => item.id !== schedule.id)?.id || "");
                        }}
                    >
                        Delete this fee schedule
                    </button>
                </>
            ) : <p className="emr__muted">No fee schedules yet.</p>}
        </div>
    );
};

const DECISION_COLORS = { approved: "#1a7f37", pended: "#b26a00", denied: "#b42318" };

const PrecertRequest = ({ data, services, onSave, onCancel }) => {
    const [request, setRequest] = useState(() => ({
        memberId: data.members?.[0]?.id || "",
        providerId: "",
        serviceKey: "advanceDiagnostic",
        code: "72148",
        diagnosisCodes: [],
        from: today(),
        units: 1,
        criteriaMet: [],
    }));
    const [result, setResult] = useState(null);
    const criteria = findCriteria(data.medicalNecessity, request.serviceKey, request.code);
    const change = (patch) => {
        const resetsCriteria = "serviceKey" in patch || "code" in patch;
        setRequest((current) => ({ ...current, ...patch, ...(resetsCriteria ? { criteriaMet: [] } : {}) }));
        setResult(null);
    };
    const toggle = (index) => change({
        criteriaMet: request.criteriaMet.includes(index) ? request.criteriaMet.filter((item) => item !== index) : [...request.criteriaMet, index],
    });
    const review = () => {
        const decision = reviewRequest({ request, sets: data.medicalNecessity });
        console.log("Mock precert review", { request, decision });
        setResult(decision);
    };
    const save = () => onSave({
        id: newId("auth"),
        number: `AUTH-${Math.floor(2000 + Math.random() * 7000)}`,
        type: "authorization",
        status: result.status,
        memberId: request.memberId,
        serviceKey: request.serviceKey,
        providerId: request.providerId,
        from: result.from,
        to: result.to,
        units: result.status === "approved" ? result.units : result.status === "pended" ? request.units : 0,
        used: 0,
        agreedAmount: null,
        notes: request.code ? `Asked for ${request.code}` : "",
        diagnosisCodes: request.diagnosisCodes,
        criteriaId: result.criteriaId,
        decision: result.reason,
    });
    return (
        <div className="emr__form">
            <p className="emr__muted">The provider asks before the service. The review checks the diagnosis and the clinical criteria, then files an authorization with the decision.</p>
            <div className="emr__grid">
                <Field label="Member"><Choice value={request.memberId} onChange={(memberId) => change({ memberId })} options={(data.members || []).map((member) => ({ value: member.id, label: `${member.name} · ${member.memberNumber}` }))} /></Field>
                <Field label="Provider"><Choice value={request.providerId} onChange={(providerId) => change({ providerId })} empty="Any provider" options={data.providers.map((provider) => ({ value: provider.id, label: provider.name }))} /></Field>
                <Field label="Service"><Choice value={request.serviceKey} onChange={(serviceKey) => change({ serviceKey })} options={services} /></Field>
                <Field label="Procedure code (optional)"><input value={request.code} placeholder="72148" onChange={(event) => change({ code: event.target.value.toUpperCase() })} /></Field>
                <div className="emr__field is-wide">
                    <span>Diagnosis codes (ICD-10)</span>
                    <DiagnosisSelect value={request.diagnosisCodes} onChange={(diagnosisCodes) => change({ diagnosisCodes })} />
                </div>
                <Field label="Starts"><input type="date" value={request.from} onChange={(event) => change({ from: event.target.value })} /></Field>
                <Field label="Units asked for"><input inputMode="numeric" value={request.units} onChange={(event) => change({ units: Number(event.target.value.replace(/\D/g, "")) || 0 })} /></Field>
            </div>
            {criteria ? (
                <div className="emr__criteria">
                    <b>{criteria.name}</b>
                    <small>
                        {criteria.mode === "all" ? "Every item has to be met." : "Any one item is enough."}
                        {criteria.maxUnits ? ` Up to ${criteria.maxUnits} ${criteria.maxUnits === 1 ? "unit" : "units"}.` : ""} Good for {criteria.validDays} days.
                    </small>
                    {criteria.criteria.map((text, index) => (
                        <label key={text} className="emr__toggle">
                            <input type="checkbox" checked={request.criteriaMet.includes(index)} onChange={() => toggle(index)} />
                            {text}
                        </label>
                    ))}
                </div>
            ) : (
                <p className="emr__muted">No medical necessity criteria cover this service. The request is approved as asked.</p>
            )}
            {result ? (
                <div className="emr__decision" style={{ "--decision-color": DECISION_COLORS[result.status] }}>
                    <b>{AUTH_STATUSES.find((entry) => entry.value === result.status)?.label || result.status}</b>
                    <p>{result.reason}</p>
                    {result.checks.length ? (
                        <ul>
                            {result.checks.map((check) => (
                                <li key={check.label} className={check.met ? "is-met" : undefined}>{check.met ? "Met" : "Not met"}: {check.label}</li>
                            ))}
                        </ul>
                    ) : null}
                    {result.status === "approved" ? <small>{result.units} {result.units === 1 ? "unit" : "units"}, {result.from} to {result.to}</small> : null}
                </div>
            ) : null}
            <div className="emr__actions">
                <button type="button" className="emr__secondary" onClick={onCancel}>Cancel</button>
                {result ? (
                    <button type="button" className="emr__primary" onClick={save}>Save authorization</button>
                ) : (
                    <button type="button" className="emr__primary" disabled={!request.memberId || !request.serviceKey || !request.diagnosisCodes.length} onClick={review}>Review</button>
                )}
            </div>
        </div>
    );
};

const NecessityView = ({ data, update, services }) => {
    const [draft, setDraft] = useState(null);
    const sets = data.medicalNecessity || [];
    const edit = (item) => setDraft({ ...structuredClone(item), codesText: item.codes.join(", "), criteriaText: item.criteria.join("\n") });
    const save = () => {
        const { codesText, criteriaText, ...item } = draft;
        const next = {
            ...item,
            name: item.name.trim(),
            codes: codesText.split(/[\s,]+/).map((code) => code.trim().toUpperCase()).filter(Boolean),
            criteria: criteriaText.split("\n").map((line) => line.trim()).filter(Boolean),
            maxUnits: Number(item.maxUnits) || null,
            validDays: Number(item.validDays) || 90,
        };
        update({ ...data, medicalNecessity: upsert(sets, next) });
        setDraft(null);
    };
    return (
        <>
            <p className="emr__muted">
                Criteria sets say when a service is medically necessary. A precert request is reviewed against them. On a claim, step 6 denies the line when the diagnosis doesn't support the service.
            </p>
            <div className="emr__toolbar">
                <p className="emr__muted">{sets.length} criteria sets</p>
                <button
                    type="button"
                    className="emr__link"
                    onClick={() => setDraft({ id: newId("mn"), name: "", serviceKey: services[0]?.value || "", codes: [], diagnoses: [], criteria: [], mode: "any", maxUnits: null, validDays: 90, notMet: "pend", codesText: "", criteriaText: "" })}
                >
                    <LuPlus /> Add criteria set
                </button>
            </div>
            {draft ? (
                <div className="emr__form">
                    <div className="emr__grid">
                        <Field label="Name" wide><input value={draft.name} placeholder="Lumbar spine MRI" onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></Field>
                        <Field label="Service"><Choice value={draft.serviceKey} onChange={(serviceKey) => setDraft({ ...draft, serviceKey })} options={services} /></Field>
                        <Field label="Procedure codes"><input value={draft.codesText} placeholder="Blank covers the whole service" onChange={(event) => setDraft({ ...draft, codesText: event.target.value })} /></Field>
                        <div className="emr__field is-wide">
                            <span>Supporting diagnoses</span>
                            <DiagnosisSelect value={draft.diagnoses} placeholder="Blank means any diagnosis" onChange={(diagnoses) => setDraft({ ...draft, diagnoses })} />
                        </div>
                        <Field label="Criteria, one per line" wide>
                            <textarea rows={4} value={draft.criteriaText} onChange={(event) => setDraft({ ...draft, criteriaText: event.target.value })} />
                        </Field>
                        <Field label="Needs"><Choice value={draft.mode} onChange={(mode) => setDraft({ ...draft, mode })} options={NECESSITY_MODES} /></Field>
                        <Field label="When not met"><Choice value={draft.notMet} onChange={(notMet) => setDraft({ ...draft, notMet })} options={NECESSITY_NOT_MET} /></Field>
                        <Field label="Most units per approval"><input inputMode="numeric" value={draft.maxUnits ?? ""} placeholder="No cap" onChange={(event) => setDraft({ ...draft, maxUnits: event.target.value.replace(/\D/g, "") })} /></Field>
                        <Field label="Good for (days)"><input inputMode="numeric" value={draft.validDays ?? ""} onChange={(event) => setDraft({ ...draft, validDays: event.target.value.replace(/\D/g, "") })} /></Field>
                    </div>
                    <Actions onCancel={() => setDraft(null)} onSave={save} disabled={!draft.name.trim() || !draft.serviceKey} label="Save criteria set" />
                </div>
            ) : null}
            <ul className="emr__list">
                {sets.map((item) => (
                    <li key={item.id}>
                        <div>
                            <b>{item.name}</b>
                            <small>{serviceName(item.serviceKey)}{item.codes.length ? ` · ${item.codes.join(", ")}` : ""}</small>
                            <small>{item.diagnoses.length ? `Diagnoses: ${item.diagnoses.join(", ")}` : "Any diagnosis"}</small>
                            <small>
                                {item.criteria.length} criteria, {item.mode === "all" ? "all needed" : "any one"}
                                {item.maxUnits ? ` · up to ${item.maxUnits} ${item.maxUnits === 1 ? "unit" : "units"}` : ""} · {item.validDays} days · {item.notMet === "deny" ? "denies" : "pends"} when not met
                            </small>
                        </div>
                        <button type="button" className="emr__link" onClick={() => edit(item)}>Edit</button>
                        <button type="button" className="emr__icon" aria-label={`Delete ${item.name}`} onClick={() => update({ ...data, medicalNecessity: sets.filter((entry) => entry.id !== item.id) })}>
                            <LuTrash2 />
                        </button>
                    </li>
                ))}
            </ul>
        </>
    );
};

const UmTab = ({ data, update }) => {
    const services = useServiceOptions();
    const [view, setView] = useState("rules");
    const [rule, setRule] = useState(null);
    const [auth, setAuth] = useState(null);
    const [requesting, setRequesting] = useState(false);
    const networks = data.networks.filter((network) => network.kind !== "reference");
    const memberName = (id) => data.members?.find((member) => member.id === id)?.name || "Unknown member";
    const providerName = (id) => (id ? data.providers.find((provider) => provider.id === id)?.name || "Unknown provider" : "Any provider");
    const penaltyText = (penalty) => (penalty?.valueType === "percent" ? `${penalty.value}%` : money(penalty?.value));
    const saveRule = () => {
        update({ ...data, umRules: upsert(data.umRules || [], { ...rule, name: rule.name.trim(), networkId: rule.source === "contract" ? rule.networkId : "", penalty: rule.missingAuth === "penalty" ? rule.penalty : null }) });
        setRule(null);
    };
    const saveAuth = () => {
        update({ ...data, authorizations: upsert(data.authorizations || [], { ...auth, number: auth.number.trim() }) });
        setAuth(null);
    };
    return (
        <div className="emr__stack">
            <div className="emr__segment" role="group" aria-label="UM view">
                <button type="button" aria-pressed={view === "rules"} className={view === "rules" ? "is-on" : undefined} onClick={() => setView("rules")}>Rules</button>
                <button type="button" aria-pressed={view === "auths"} className={view === "auths" ? "is-on" : undefined} onClick={() => setView("auths")}>Authorizations and referrals</button>
                <button type="button" aria-pressed={view === "necessity"} className={view === "necessity" ? "is-on" : undefined} onClick={() => setView("necessity")}>Medical necessity</button>
            </div>

            {view === "necessity" ? <NecessityView data={data} update={update} services={services} /> : view === "rules" ? (
                <>
                    <p className="emr__muted">
                        Contract rules run at step 10, and the provider takes the penalty. Plan rules run at step 19, and the member takes the penalty.
                        Approval and referral rules written on the plan itself also run at step 19.
                    </p>
                    <div className="emr__toolbar">
                        <p className="emr__muted">{(data.umRules || []).length} rules</p>
                        <button
                            type="button"
                            className="emr__link"
                            onClick={() => setRule({ id: newId("um"), name: "", source: "plan", networkId: networks[0]?.id || "", serviceKey: services[0]?.value || "", when: "always", missingAuth: "penalty", penalty: { valueType: "amount", value: 100 } })}
                        >
                            <LuPlus /> Add rule
                        </button>
                    </div>
                    {rule ? (
                        <div className="emr__form">
                            <div className="emr__grid">
                                <Field label="Rule name" wide><input value={rule.name} placeholder="MRI and CT need approval" onChange={(event) => setRule({ ...rule, name: event.target.value })} /></Field>
                                <Field label="Comes from"><Choice value={rule.source} onChange={(source) => setRule({ ...rule, source })} options={UM_SOURCES} /></Field>
                                {rule.source === "contract" ? (
                                    <Field label="Contract network"><Choice value={rule.networkId} onChange={(networkId) => setRule({ ...rule, networkId })} options={networks.map((network) => ({ value: network.id, label: network.name }))} /></Field>
                                ) : <span />}
                                <Field label="Service"><Choice value={rule.serviceKey} onChange={(serviceKey) => setRule({ ...rule, serviceKey })} options={services} /></Field>
                                <Field label="Applies"><Choice value={rule.when} onChange={(when) => setRule({ ...rule, when })} options={UM_WHEN} /></Field>
                                <Field label="With no approval"><Choice value={rule.missingAuth} onChange={(missingAuth) => setRule({ ...rule, missingAuth, penalty: rule.penalty || { valueType: "amount", value: 100 } })} options={UM_MISSING} /></Field>
                                {rule.missingAuth === "penalty" ? (
                                    <div className="emr__pair">
                                        <Field label="Penalty">
                                            <input inputMode="decimal" value={rule.penalty?.value ?? ""} onChange={(event) => setRule({ ...rule, penalty: { ...rule.penalty, value: Number(digits(event.target.value)) || 0 } })} />
                                        </Field>
                                        <Field label="As">
                                            <Choice value={rule.penalty?.valueType} onChange={(valueType) => setRule({ ...rule, penalty: { ...rule.penalty, valueType } })} options={[{ value: "amount", label: "Dollars" }, { value: "percent", label: "% of allowed" }]} />
                                        </Field>
                                    </div>
                                ) : null}
                            </div>
                            <Actions onCancel={() => setRule(null)} onSave={saveRule} disabled={!rule.name.trim() || !rule.serviceKey} label="Save rule" />
                        </div>
                    ) : null}
                    <ul className="emr__list">
                        {(data.umRules || []).map((item) => (
                            <li key={item.id}>
                                <div>
                                    <b>{item.name}</b>
                                    <small>
                                        {item.source === "contract" ? `${data.networks.find((network) => network.id === item.networkId)?.name || "?"} contract, step 10` : "Benefit plan, step 19"}
                                        {" · "}{serviceName(item.serviceKey)}{item.when === "notEmergency" ? " · not emergencies" : ""}
                                    </small>
                                    <small>
                                        With no approval: {item.missingAuth === "penalty" ? `${penaltyText(item.penalty)} penalty to the ${item.source === "contract" ? "provider" : "member"}` : item.missingAuth === "deny" ? "deny" : "hold for review"}
                                    </small>
                                </div>
                                <button type="button" className="emr__link" onClick={() => setRule(structuredClone(item))}>Edit</button>
                                <button type="button" className="emr__icon" aria-label={`Delete ${item.name}`} onClick={() => update({ ...data, umRules: data.umRules.filter((entry) => entry.id !== item.id) })}>
                                    <LuTrash2 />
                                </button>
                            </li>
                        ))}
                    </ul>
                </>
            ) : (
                <>
                    <p className="emr__muted">
                        A claim line matches an authorization on member, service, date of service, and provider. Approved units get used up as lines pay.
                        An agreed amount replaces the fee at step 14.
                    </p>
                    <div className="emr__toolbar">
                        <p className="emr__muted">{(data.authorizations || []).length} on file</p>
                        <button type="button" className="emr__link" onClick={() => { setAuth(null); setRequesting(true); }}>
                            <LuPlus /> Request approval
                        </button>
                        <button
                            type="button"
                            className="emr__link"
                            onClick={() => {
                                setRequesting(false);
                                setAuth({ id: newId("auth"), number: `AUTH-${Math.floor(2000 + Math.random() * 7000)}`, type: "authorization", status: "approved", memberId: data.members?.[0]?.id || "", serviceKey: services[0]?.value || "", providerId: "", from: "2026-01-01", to: "2026-12-31", units: 1, used: 0, agreedAmount: null, notes: "", diagnosisCodes: [] });
                            }}
                        >
                            <LuPlus /> Add authorization
                        </button>
                    </div>
                    {requesting ? (
                        <PrecertRequest
                            data={data}
                            services={services}
                            onCancel={() => setRequesting(false)}
                            onSave={(item) => {
                                update({ ...data, authorizations: [item, ...(data.authorizations || [])] });
                                setRequesting(false);
                            }}
                        />
                    ) : null}
                    {auth ? (
                        <div className="emr__form">
                            <div className="emr__grid">
                                <Field label="Number"><input value={auth.number} onChange={(event) => setAuth({ ...auth, number: event.target.value })} /></Field>
                                <Field label="Type"><Choice value={auth.type} onChange={(type) => setAuth({ ...auth, type })} options={AUTH_TYPES} /></Field>
                                <Field label="Member"><Choice value={auth.memberId} onChange={(memberId) => setAuth({ ...auth, memberId })} options={(data.members || []).map((member) => ({ value: member.id, label: `${member.name} · ${member.memberNumber}` }))} /></Field>
                                <Field label="Status"><Choice value={auth.status} onChange={(status) => setAuth({ ...auth, status })} options={AUTH_STATUSES} /></Field>
                                <Field label="Service"><Choice value={auth.serviceKey} onChange={(serviceKey) => setAuth({ ...auth, serviceKey })} options={services} /></Field>
                                <Field label="Provider"><Choice value={auth.providerId} onChange={(providerId) => setAuth({ ...auth, providerId })} empty="Any provider" options={data.providers.map((provider) => ({ value: provider.id, label: provider.name }))} /></Field>
                                <Field label="From"><input type="date" value={auth.from} onChange={(event) => setAuth({ ...auth, from: event.target.value })} /></Field>
                                <Field label="To"><input type="date" value={auth.to} onChange={(event) => setAuth({ ...auth, to: event.target.value })} /></Field>
                                <Field label="Units approved"><input inputMode="numeric" value={auth.units} onChange={(event) => setAuth({ ...auth, units: Number(event.target.value.replace(/\D/g, "")) || 0 })} /></Field>
                                <Field label="Units already used"><input inputMode="numeric" value={auth.used} onChange={(event) => setAuth({ ...auth, used: Number(event.target.value.replace(/\D/g, "")) || 0 })} /></Field>
                                <Field label="Agreed amount (optional)" wide>
                                    <input inputMode="decimal" placeholder="Leave blank to use the fee schedule" value={auth.agreedAmount ?? ""} onChange={(event) => setAuth({ ...auth, agreedAmount: event.target.value === "" ? null : Number(digits(event.target.value)) || 0 })} />
                                </Field>
                                <div className="emr__field is-wide">
                                    <span>Diagnosis codes</span>
                                    <DiagnosisSelect value={auth.diagnosisCodes || []} onChange={(diagnosisCodes) => setAuth({ ...auth, diagnosisCodes })} />
                                </div>
                                <Field label="Notes" wide><input value={auth.notes} onChange={(event) => setAuth({ ...auth, notes: event.target.value })} /></Field>
                            </div>
                            <Actions onCancel={() => setAuth(null)} onSave={saveAuth} disabled={!auth.number.trim() || !auth.memberId || !auth.serviceKey} label="Save authorization" />
                        </div>
                    ) : null}
                    <ul className="emr__list">
                        {(data.authorizations || []).map((item) => (
                            <li key={item.id}>
                                <div>
                                    <b>{item.number} · {AUTH_STATUSES.find((entry) => entry.value === item.status)?.label}</b>
                                    <small>{item.type === "referral" ? "Referral" : "Authorization"} · {serviceName(item.serviceKey)} · {memberName(item.memberId)} · {providerName(item.providerId)}</small>
                                    <small>
                                        {item.from} to {item.to} · {item.used} of {item.units} used{item.agreedAmount != null ? ` · agreed amount ${money(item.agreedAmount)}` : ""}
                                    </small>
                                    {item.diagnosisCodes?.length ? <small>Diagnosis: {item.diagnosisCodes.map(icdLabel).join(", ")}</small> : null}
                                    {item.decision ? <small>Decision: {item.decision}</small> : null}
                                    {item.notes ? <small>{item.notes}</small> : null}
                                </div>
                                <button type="button" className="emr__link" onClick={() => setAuth(structuredClone(item))}>Edit</button>
                                <button type="button" className="emr__icon" aria-label={`Delete ${item.number}`} onClick={() => update({ ...data, authorizations: data.authorizations.filter((entry) => entry.id !== item.id) })}>
                                    <LuTrash2 />
                                </button>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
};

export const EmrTabs = ({ tab, onChange }) => (
    <div className="internal-work__mode is-tabs" role="tablist" aria-label="Mock claims">
        {TABS.map((item) => (
            <button key={item.id} type="button" role="tab" aria-selected={tab === item.id} className={tab === item.id ? "is-on" : undefined} onClick={() => onChange(item.id)}>
                {item.label}
            </button>
        ))}
    </div>
);

const EmrPanel = ({ account, tab }) => {
    const [data, setData] = useState(() => loadEmr());
    const update = (next) => {
        saveEmr(next);
        setData(next);
    };
    return (
        <div className="emr">
            <div className="emr__intro">
                <h3>Mock claims</h3>
                <p>Enter a claim and watch it run through the HealthRules claim steps for this account's plans. All the data here is sample data kept in this browser.</p>
            </div>
            {tab === "claim" ? <ClaimTab account={account} data={data} /> : null}
            {tab === "members" ? <MembersTab account={account} data={data} update={update} /> : null}
            {tab === "providers" ? <ProvidersTab data={data} update={update} /> : null}
            {tab === "networks" ? <NetworksTab data={data} update={update} account={account} /> : null}
            {tab === "fees" ? <FeesTab data={data} update={update} /> : null}
            {tab === "um" ? <UmTab data={data} update={update} /> : null}
            {tab !== "claim" ? (
                <button type="button" className="emr__link is-reset" onClick={() => setData(resetEmr())}>Put back the sample data</button>
            ) : null}
        </div>
    );
};

export default EmrPanel;
